import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getSsoStatus } from '../services/api';
import BrandMark from '../components/BrandMark';
import './Login.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Login = () => {
  const { login: loginGitHub, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ssoStatus, setSsoStatus] = useState({ samlEnabled: false, githubOAuthEnabled: true });
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: '',
    displayName: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getSsoStatus().then((res) => setSsoStatus(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const err = searchParams.get('error');
    if (err === 'github_oauth') {
      setError('GitHub sign-in failed (often due to callback URL or app settings). Try again or use email/password. Contact your administrator if it persists.');
      setSearchParams({}); // clear from URL
    } else if (err === 'github_denied') {
      setError('GitHub authorization was cancelled or denied.');
      setSearchParams({});
    } else if (err === 'login_failed') {
      setError('Sign-in could not be completed. Please try again.');
      setSearchParams({});
    } else if (err === 'saml') {
      setError('SSO sign-in failed. Contact your administrator.');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Login
      const response = await axios.post(`${API_URL}/auth/login`, {
        email: formData.email,
        password: formData.password
      }, {
        withCredentials: true
      });

      if (response.data.requiresPasswordChange) {
        // Redirect to force password change
        navigate('/force-password-change');
      } else if (response.data.requiresMFA) {
        // Redirect to MFA verification
        navigate('/verify-mfa');
      } else {
        // Login successful
        await checkAuth();
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <BrandMark size={48} />
          <h1>Access Manager</h1>
        </div>
        <p className="login-description">
          Sign in to see your repositories, request access, or approve a request.
        </p>
        <p className="signup-note">
          You cannot create an account here. An administrator will create your account and you will receive your login credentials (including GitHub username/ID) by email.
        </p>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              placeholder="email@example.com"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              minLength={8}
              placeholder="Password"
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary btn-large"
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="divider">
          <span>OR</span>
        </div>

        <button onClick={loginGitHub} className="btn btn-secondary btn-large">
          Continue with GitHub account
        </button>

        {ssoStatus.samlEnabled && (
          <>
            <div className="divider"><span>OR</span></div>
            <a href={`${API_URL}/auth/saml`} className="btn btn-secondary btn-large sso-btn">
              Sign in with SSO (SAML)
            </a>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
